import React, { useMemo } from "react";
import { AgChartOptions, AgDonutSeriesOptions } from "ag-charts-community";
import { AgCharts } from "ag-charts-react";

import theme from "fond/theme";
import { CostBreakdown } from "fond/types";
import { formatCurrency } from "fond/utils/currency";

interface IProps {
  totalCost: number | null;
  data: CostBreakdown[];
}

const CostBreakdownChart: React.FC<IProps> = ({ totalCost, data }) => {
  const summarizedData = data.reduce(
    (currentList, next) => {
      const copyOfCurrentList = [...currentList];

      const updateItem = (tag: string, cost: number) => {
        const targetIndex = currentList.findIndex((item) => item.tag === tag);
        if (targetIndex === -1) {
          copyOfCurrentList.push({ tag, cost });
        } else {
          copyOfCurrentList[targetIndex].cost += cost;
        }
      };

      if (next.Tags.length === 0) {
        updateItem("Untagged", next.TotalCost);
      } else {
        next.Tags.forEach((tag) => {
          updateItem(tag, next.TotalCost);
        });
      }

      return copyOfCurrentList;
    },
    [] as Array<{ tag: string; cost: number }>
  );

  const series: AgDonutSeriesOptions[] = useMemo(
    () => [
      {
        type: "donut",
        calloutLabelKey: "tag",
        calloutLabel: { enabled: false },
        angleKey: "cost",
        innerRadiusRatio: 0.6,
        innerLabels: [
          {
            text: formatCurrency(totalCost, { notation: "compact" }),
            fontSize: 20,
            fontWeight: 700,
            fontFamily: theme.typography.fontFamily,
          },
          {
            text: "Total Cost",
            fontSize: 16,
            fontFamily: theme.typography.fontFamily,
            spacing: 8,
            color: "rgba(0, 0, 0, 0.45)",
          },
        ],
        tooltip: {
          renderer: (params) => {
            return {
              heading: "Total cost",
              title: `${params.datum.tag}: ${formatCurrency(params.datum.cost)}`,
              data: [],
            };
          },
        },
      },
    ],
    [totalCost]
  );

  const options: AgChartOptions = useMemo(
    () => ({
      data: summarizedData,
      series,
    }),
    [summarizedData, series]
  );

  return <AgCharts options={options} style={{ height: "100%" }} />;
};

export default CostBreakdownChart;
